import { format } from 'date-fns'
import { StatusBadge } from '@/components/ui/status-badge'
import type { EmailRecord } from '@/lib/types'

const SUBJECT_MAX_LENGTH = 50
const RECIPIENT_MAX_LENGTH = 30

function truncate(str: string, max: number): string {
  return str.length > max ? `${str.slice(0, max)}...` : str
}

interface EmailTableProps {
  data: EmailRecord[]
  isLoading: boolean
  onRowClick: (email: EmailRecord) => void
  pagination: {
    page: number
    perPage: number
    total: number
  } | null
  onPageChange: (page: number) => void
  onPerPageChange: (perPage: number) => void
}

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const

export function EmailTable({
  data,
  isLoading,
  onRowClick,
  pagination,
  onPageChange,
  onPerPageChange,
}: EmailTableProps): React.JSX.Element {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-admin-500 border-t-transparent" />
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center rounded-xl border border-surface-3 bg-surface-0 py-16">
        <p className="text-text-secondary">No emails found matching your filters.</p>
      </div>
    )
  }

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.perPage) : 1
  const showStart = pagination ? (pagination.page - 1) * pagination.perPage + 1 : 1
  const showEnd = pagination
    ? Math.min(pagination.page * pagination.perPage, pagination.total)
    : data.length

  return (
    <div className="overflow-hidden rounded-xl border border-surface-3 bg-surface-0">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-surface-3 bg-surface-1">
            <tr>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Timestamp
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                From
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                To
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Subject
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Status
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Account
              </th>
              <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-text-secondary">
                Domain
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-2">
            {data.map((email) => (
              <tr
                key={email.id}
                onClick={() => onRowClick(email)}
                className="cursor-pointer transition-colors hover:bg-surface-1"
              >
                <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                  {format(new Date(email.created_at), 'MMM d, HH:mm:ss')}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-primary">
                  {email.from_address}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                  {truncate(email.to_addresses[0] ?? '', RECIPIENT_MAX_LENGTH)}
                  {email.to_addresses.length > 1 && (
                    <span className="ml-1 text-xs text-text-tertiary">
                      +{email.to_addresses.length - 1}
                    </span>
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-primary">
                  {truncate(email.subject, SUBJECT_MAX_LENGTH)}
                </td>
                <td className="whitespace-nowrap px-4 py-3">
                  <StatusBadge status={email.status} />
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-secondary">
                  {email.account_email}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-text-secondary">{email.domain}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && (
        <div className="flex items-center justify-between border-t border-surface-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <p className="text-sm text-text-secondary">
              {showStart}-{showEnd} of {pagination.total.toLocaleString()}
            </p>
            <select
              value={pagination.perPage}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
              className="rounded-lg border border-surface-3 bg-surface-0 px-2 py-1 text-xs text-text-secondary focus:border-admin-500 focus:outline-none"
            >
              {PAGE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => onPageChange(pagination.page - 1)}
              className="rounded-lg border border-surface-3 px-3 py-1 text-xs text-text-secondary hover:bg-surface-1 disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-xs text-text-secondary">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={pagination.page >= totalPages}
              onClick={() => onPageChange(pagination.page + 1)}
              className="rounded-lg border border-surface-3 px-3 py-1 text-xs text-text-secondary hover:bg-surface-1 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
